import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { NavItem } from '../model/nav-item';
import { URI } from '../settings/uri-constants';
import { LoginService } from './login.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    constructor(private router: Router, private loginService: LoginService) { }

    private getEnterpriseType(): 'supplier' | 'wholesaler' | null {
        const sessionUser = this.loginService.sessionUser.value;
        if (!sessionUser?.isEnterprise()) return null;

        return sessionUser.isSupplierEnterprise() ? 'supplier' : 'wholesaler';
    }

    getNavigationItems(): NavItem[] {
        return [
            {
                name: 'Dashboard',
                path: URI.getRouteNavPath('dashboard'),
                iconClass: 'dashboard-image-state',
                children: [],
            },
            {
                name: 'Manage Users',
                path: URI.getRouteNavPath('manageUsers'),
                iconClass: 'user-image-state',
                children: [],
            },
            {
                name: 'Transactions',
                path: URI.getRouteNavPath('adminViewTransactions'),
                iconClass: 'transaction-image-state',
                children: [],
            },
            {
                name: 'Report',
                path: '',
                iconClass: 'admin-ttb-image-state',
                children: [
                    {
                        name: 'Cashback Report',
                        path: URI.getRouteNavPath('adminCashbackReport'),
                        iconClass: 'transaction-image-state',
                    },
                    {
                        name: 'Sales Report',
                        path: URI.getRouteNavPath('adminSalesReport'),
                        iconClass: 'smart-safes-image-state',
                    },
                ],
            },
            {
                name: 'Transfer To Bank Requests',
                path: URI.getRouteNavPath('adminTransferToBank'),
                iconClass: 'admin-ttb-image-state',
                children: [],
            },
            {
                name: 'Settings',
                path: URI.getRouteNavPath('adminSettings'),
                iconClass: 'settings-image-state',
                children: [],
            },
            {
                name: 'EFT and ATM Cash In',
                path: URI.getRouteNavPath('adminEFTCashIn'),
                iconClass: 'admin-ttb-image-state',
                children: [],
            },
            {
                name: 'Smart Safes',
                path: URI.getRouteNavPath('adminSmartSafes'),
                iconClass: 'smart-safe-image-state',
                children: [],
            },
            {
                name: 'Agent Applications',
                path: URI.getRouteNavPath('adminAgentApplications'),
                iconClass: 'agent-image-state',
                children: [],
            },
            {
                name: 'GRV',
                path: URI.getRouteNavPath('adminGRV'),
                iconClass: 'agent-image-state',
                children: [],
            },
            {
                name: 'RMS Simulator',
                path: URI.getRouteNavPath('adminRmsSimulator'),
                iconClass: 'fa fa-shopping-cart',
                isFaIcon: true,
                children: [],
            },
            {
                name: 'Banners',
                path: URI.getRouteNavPath('adminBanners'),
                iconClass: 'agent-image-state',
                children: [],
            },
        ];
    }

    /**
     * Returns navigation items for the enterprise context based on the type (supplier or wholesaler).
     * @param type - The type of enterprise ('supplier' or 'wholesaler').
     */
    getEnterpriseNavItems(): NavItem[] {
        const type = this.getEnterpriseType();

        const items: NavItem[] = [
            {
                name: 'Dashboard',
                path: URI.getRouteNavPath('enterpriseDashboard'),
                iconClass: 'dashboard-image-state',
                children: [],
                hiddenFor: ['wholesaler'],
            },
            {
                name: 'Profile',
                path: URI.getRouteNavPath('enterpriseProfile'),
                iconClass: 'user-image-state',
                children: [],
                hiddenFor: [],
            },
            {
                name: 'Transfer To Bank Requests',
                path: URI.getRouteNavPath('enterpriseTransferToBank'),
                iconClass: 'admin-ttb-image-state',
                children: [],
                hiddenFor: [],
            },
            {
                name: 'Cashbacks Redeemed',
                path: URI.getRouteNavPath('enterpriseCashbacksRedeemed'),
                iconClass: 'transaction-image-state',
                children: [],
                hiddenFor: ['supplier'],
            },
            {
                name: 'Report',
                path: '',
                iconClass: 'admin-ttb-image-state',
                children: [
                    {
                        name: 'Cashback Report',
                        path: URI.getRouteNavPath('enterpriseCashbackReport'),
                        iconClass: 'transaction-image-state',
                        hiddenFor: [],
                    },
                    {
                        name: 'Sales Report',
                        path: URI.getRouteNavPath('enterpriseSalesReport'),
                        iconClass: 'smart-safes-image-state',
                        hiddenFor: [],
                    },
                ],
                hiddenFor: ['wholesaler'],
            },
            {
                name: 'Wallet Balance',
                path: URI.getRouteNavPath('enterpriseWalletBalance'),
                iconClass: 'fa fa-credit-card',
                isFaIcon: true,
                children: [],
                hiddenFor: ['wholesaler'],
            },
        ];

        // If type can't be determined, return all items unfiltered
        if (!type) return items;

        // Filter based on hiddenFor
        return items
            .filter(item => !item.hiddenFor || !item.hiddenFor.includes(type))
            .map(item => ({
                ...item,
                children: item.children?.filter(
                    child => !child.hiddenFor || !child.hiddenFor.includes(type)
                ),
            }));
    }


    updateExpandedMenusByUrl(
        url: string,
        navigationItems: any[],
        manuallyToggled: Record<string, boolean>,
        expandedMenus: Record<string, boolean>
    ): void {
        navigationItems.forEach((navItem) => {
            if (navItem.children && navItem.children.length > 0) {
                if (!manuallyToggled[navItem.name]) {
                    expandedMenus[navItem.name] = navItem.children.some((child) =>
                        url.startsWith(child.path),
                    );
                }
            }
        });
    }

    updateActiveParents(
        navigationItems: any[],
        hasActiveChild: Record<string, boolean>
    ): void {
        navigationItems.forEach((navItem) => {
            if (navItem.children?.length) {
                hasActiveChild[navItem.name] = navItem.children.some((child) =>
                    this.router.isActive(child.path, true)
                );
            } else {
                hasActiveChild[navItem.name] = false;
            }
        });
    }

    toggleMenu(
        menuName: string,
        expandedMenus: Record<string, boolean>,
        manuallyToggled: Record<string, boolean>,
        event?: MouseEvent
    ): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        expandedMenus[menuName] = !expandedMenus[menuName];
        manuallyToggled[menuName] = true;
    }
}
